<template>
  <div
    class="multiple-select"
    :class="`variant-${props.variant}`"
    ref="selectorRef"
  >
    <div
      class="select-trigger"
      :class="[`variant-${props.variant}`, { active: isOpen }]"
      @click="toggleDropdown"
    >
      <div class="trigger-content">
        <span
          v-if="props.showCountBadge && selectedItems.length > 0"
          class="item-counter"
        >
          {{ displayCount }}
        </span>
        <span class="select-text">
          <span v-if="props.prefix" class="prefix-text">{{
            props.prefix
          }}</span>
          <span>{{ triggerText }}</span>
        </span>
      </div>
      <svg
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
      >
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <Transition name="dropdown">
      <Teleport v-if="props.teleport" to="body">
        <div
          v-if="isOpen && positionReady"
          class="select-dropdown"
          :class="`variant-${props.variant}`"
          :style="teleportedDropdownStyles"
          ref="dropdownRef"
        >
          <div v-if="showSearch" class="search-container">
            <div class="search-input-wrapper" @click.stop>
              <SearchIcon width="20" height="20" color="#6C737F" />
              <input
                v-model="searchQuery"
                class="search-input"
                type="text"
                :placeholder="props.inputPlaceholder"
              />
            </div>
            <Transition name="clear-button">
              <button
                v-if="selectedItems.length > 0"
                class="clear-button"
                @click.stop="clearSelection"
                type="button"
              >
                Clear
              </button>
            </Transition>
          </div>
          <div class="options-container" :style="optionsContainerStyles">
            <div
              v-if="filteredOptions.length === 0"
              class="select-option empty-state"
            >
              <span class="option-text">{{ props.emptyText }}</span>
            </div>
            <div
              v-else
              v-for="option in filteredOptions"
              :key="getOptionValue(option)"
              class="select-option"
              :class="{
                selected: isSelected(getOptionValue(option)),
                disabled: option.disabled,
              }"
              @click="handleOptionClick(option, $event)"
            >
              <div class="option-content">
                <span class="option-text">{{ getOptionText(option) }}</span>
                <input
                  type="checkbox"
                  :checked="isSelected(getOptionValue(option))"
                  :disabled="option.disabled"
                  @change="toggleSelection(option)"
                  @click.stop
                />
              </div>
            </div>
          </div>
        </div>
      </Teleport>
      <div
        v-else-if="isOpen"
        class="select-dropdown"
        :class="`variant-${props.variant}`"
        :style="dropdownStyles"
      >
        <div v-if="showSearch" class="search-container">
          <div class="search-input-wrapper" @click.stop>
            <SearchIcon width="20" height="20" color="#6C737F" />
            <input
              v-model="searchQuery"
              class="search-input"
              type="text"
              :placeholder="props.inputPlaceholder"
            />
          </div>
          <Transition name="clear-button">
            <button
              v-if="selectedItems.length > 0"
              class="clear-button"
              @click.stop="clearSelection"
              type="button"
            >
              Clear
            </button>
          </Transition>
        </div>
        <div class="options-container" :style="optionsContainerStyles">
          <div
            v-if="filteredOptions.length === 0"
            class="select-option empty-state"
          >
            <span class="option-text">{{ props.emptyText }}</span>
          </div>
          <div
            v-else
            v-for="option in filteredOptions"
            :key="getOptionValue(option)"
            class="select-option"
            :class="{
              selected: isSelected(getOptionValue(option)),
              disabled: option.disabled,
            }"
            @click="handleOptionClick(option, $event)"
          >
            <div class="option-content">
              <span class="option-text">{{ getOptionText(option) }}</span>
              <input
                type="checkbox"
                :checked="isSelected(getOptionValue(option))"
                :disabled="option.disabled"
                @change="toggleSelection(option)"
                @click.stop
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  defineProps,
  defineEmits,
  watch,
  defineExpose,
  nextTick,
} from "vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Select items",
  },
  inputPlaceholder: {
    type: String,
    default: "Search...",
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,
    default: "400px",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxSelections: {
    type: Number,
    default: Infinity,
  },
  prefix: {
    type: String,
    default: "",
  },
  selectedTextSingular: {
    type: String,
    default: "item selected",
  },
  selectedTextPlural: {
    type: String,
    default: "items selected",
  },
  allSelectedText: {
    type: String,
    default: "All",
  },
  showCountBadge: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) =>
      ["default", "dark", "form", "bordered"].includes(value),
  },
  emptyText: {
    type: String,
    default: "No items found",
  },
  pinnedValues: {
    type: Array,
    default: () => ["all"],
  },
  teleport: {
    type: Boolean,
    default: false,
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  order: {
    type: String,
    default: "",
    validator: (value) => ["", "a-z", "z-a"].includes(value),
  },
});

const emit = defineEmits(["update:modelValue", "change", "clear"]);

const selectorRef = ref(null);
const dropdownRef = ref(null);
const isOpen = ref(false);
const searchQuery = ref("");
const sortedOptions = ref([]);
const positionReady = ref(false);
const dropdownPosition = ref({ top: 0, left: 0, width: 0, bottom: 0 });
const isDropUp = ref(false);

const selectedItems = computed(() => props.modelValue || []);

const displayCount = computed(() => {
  const count = selectedItems.value.length;
  if (count > 9) return "9+";
  return String(count);
});

const triggerText = computed(() => {
  if (selectedItems.value.includes("all")) {
    return props.allSelectedText;
  }

  const count = selectedItems.value.length;
  if (count === 0) return props.placeholder;

  return `${displayCount.value} ${
    count === 1 ? props.selectedTextSingular : props.selectedTextPlural
  }`;
});

const updateSortedOptions = () => {
  if (!props.options?.length) {
    sortedOptions.value = [];
    return;
  }
  const base = [...props.options];
  if (props.order) {
    base.sort((a, b) => {
      const cmp = getOptionText(a).localeCompare(getOptionText(b));
      return props.order === "z-a" ? -cmp : cmp;
    });
  }
  sortedOptions.value = base.sort((a, b) => {
    const valueA = getOptionValue(a);
    const valueB = getOptionValue(b);

    const isPinnedA = props.pinnedValues.includes(valueA);
    const isPinnedB = props.pinnedValues.includes(valueB);

    if (isPinnedA && !isPinnedB) return -1;
    if (!isPinnedA && isPinnedB) return 1;
    if (isPinnedA && isPinnedB) return 0;

    const isSelectedA = isSelected(valueA);
    const isSelectedB = isSelected(valueB);

    if (isSelectedA && !isSelectedB) return -1;
    if (!isSelectedA && isSelectedB) return 1;
    return 0;
  });
};

watch(
  () => props.options,
  () => {
    if (isOpen.value) {
      updateSortedOptions();
    }
  }
);

const filteredOptions = computed(() => {
  let options = sortedOptions.value;

  if (searchQuery.value) {
    options = options.filter((option) =>
      getOptionText(option)
        .toLowerCase()
        .includes(searchQuery.value.toLowerCase())
    );
  }

  return options;
});

const dropdownStyles = computed(() => ({
  maxHeight: props.maxHeight,
}));

const teleportedDropdownStyles = computed(() => {
  const baseStyles = {
    maxHeight: props.maxHeight,
    position: "fixed",
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
    minWidth: props.matchTriggerWidth ? "0" : undefined,
    zIndex: 10000,
  };

  if (isDropUp.value) {
    baseStyles.bottom = `${dropdownPosition.value.bottom}px`;
    baseStyles.top = "auto";
  } else {
    baseStyles.top = `${dropdownPosition.value.top}px`;
  }

  return baseStyles;
});

const updateDropdownPosition = () => {
  if (!props.teleport || !selectorRef.value || !isOpen.value) {
    positionReady.value = false;
    return;
  }

  positionReady.value = false;

  nextTick(() => {
    const trigger = selectorRef.value?.querySelector(".select-trigger");
    if (!trigger) {
      positionReady.value = false;
      return;
    }

    const rect = trigger.getBoundingClientRect();

    let top = rect.bottom + 4;
    let left = rect.left;
    let width = rect.width;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const maxHeight = parseInt(props.maxHeight) || 400;
    const minWidthRequired = props.matchTriggerWidth ? 0 : 360;
    const actualWidth = props.matchTriggerWidth
      ? width
      : Math.max(minWidthRequired, width);

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < maxHeight && spaceAbove > spaceBelow) {
      isDropUp.value = true;
      const bottom = viewportHeight - rect.top + 4;

      if (left + actualWidth > viewportWidth) {
        left = viewportWidth - actualWidth - 10;
      }

      dropdownPosition.value = {
        top: 0,
        left: Math.max(10, left),
        width: actualWidth,
        bottom: bottom,
      };
    } else {
      isDropUp.value = false;

      if (left + actualWidth > viewportWidth) {
        left = viewportWidth - actualWidth - 10;
      }
      if (left < 0) {
        left = 10;
      }

      dropdownPosition.value = {
        top: Math.max(10, top),
        left: Math.max(10, left),
        width: actualWidth,
        bottom: 0,
      };
    }

    positionReady.value = true;
  });
};

const optionsContainerStyles = computed(() => ({
  maxHeight: `calc(${props.maxHeight} - ${props.showSearch ? "50px" : "0px"})`,
}));

// Helper functions to handle different option formats
const getOptionValue = (option) => {
  return option?.value !== undefined ? option.value : option;
};

const getOptionText = (option) => {
  return option?.text || option?.title || option?.label || String(option);
};

const isSelected = (value) => {
  const isAllState = selectedItems.value.includes("all");

  if (isAllState) {
    return true;
  }
  return selectedItems.value.includes(value);
};

const toggleDropdown = () => {
  if (props.disabled) return;
  const wasOpen = isOpen.value;
  if (!wasOpen) {
    updateSortedOptions();
  }
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = "";
    if (props.teleport) {
      positionReady.value = false;
      updateDropdownPosition();
    }
  } else {
    positionReady.value = false;
  }
};

const handleOptionClick = (option, event) => {
  const target = event.target;
  if (target?.closest?.('input[type="checkbox"]')) {
    return;
  }
  toggleSelection(option);
};

const toggleSelection = (option) => {
  if (option.disabled) return;

  const value = getOptionValue(option);
  const isAllState = selectedItems.value.includes("all");

  // Handle "all" option selection
  if (value === "all") {
    if (!isAllState) {
      // Select All
      emit("update:modelValue", ["all"]);
      emit("change", {
        value: ["all"],
        option,
        action: "select_all",
      });
    } else {
      // Deselect All (empty)
      emit("update:modelValue", []);
      emit("change", {
        value: [],
        option,
        action: "deselect_all",
      });
    }
    return;
  }

  let newSelection = [...selectedItems.value];

  if (isAllState) {
    // If we are in "All" state, checking an option actually means UNCHECKING it
    // So we need to select all other options
    newSelection = props.options
      .map((opt) => getOptionValue(opt))
      .filter((v) => v !== "all" && v !== value);

    emit("update:modelValue", newSelection);
    emit("change", {
      value: newSelection,
      option,
      action: "deselect_one_from_all",
    });
  } else {
    // Normal toggle behavior
    const index = newSelection.indexOf(value);

    if (index === -1) {
      // Add selection if under limit
      if (newSelection.length < props.maxSelections) {
        newSelection.push(value);
      }
    } else {
      // Remove selection
      newSelection.splice(index, 1);
    }

    emit("update:modelValue", newSelection);
    emit("change", {
      value: newSelection,
      option,
      action: index === -1 ? "add" : "remove",
    });
  }
};

const clearSelection = () => {
  emit("update:modelValue", []);
  emit("clear");
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (!isOpen.value) return;
  const clickedInsideSelector = selectorRef.value?.contains(event.target);
  const clickedInsideDropdown = props.teleport
    ? dropdownRef.value?.contains(event.target)
    : selectorRef.value
        ?.querySelector(".select-dropdown")
        ?.contains(event.target);

  if (!clickedInsideSelector && !clickedInsideDropdown) {
    isOpen.value = false;
    searchQuery.value = "";
    positionReady.value = false;
  }
};

const handleResize = () => {
  if (isOpen.value && props.teleport) {
    updateDropdownPosition();
  }
};

const handleScroll = (event) => {
  if (!isOpen.value || !props.teleport) return;

  const target = event.target;
  const dropdownElement = dropdownRef.value;
  if (!dropdownElement) return;

  const targetIsNode = target && typeof target.nodeType === "number";
  const isScrollingInsideDropdown =
    targetIsNode && dropdownElement.contains(target);

  if (!isScrollingInsideDropdown) {
    isOpen.value = false;
    searchQuery.value = "";
    positionReady.value = false;
  }
};

watch(isOpen, (newValue) => {
  if (newValue && props.teleport) {
    window.addEventListener("resize", handleResize);
    document.addEventListener("scroll", handleScroll, true);
  } else {
    window.removeEventListener("resize", handleResize);
    document.removeEventListener("scroll", handleScroll, true);
  }
});

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue || !Array.isArray(newValue)) {
      emit("update:modelValue", []);
    }
  },
  { immediate: true }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  document.removeEventListener("scroll", handleScroll, true);
});

// Expose methods for parent components
defineExpose({
  clearSelection,
  toggleDropdown,
});
</script>

<style scoped>
.multiple-select {
  position: relative;
  z-index: 1;
  width: 100%;
}

.multiple-select:has(.select-trigger.active) {
  z-index: 9999;
}

.multiple-select.variant-form {
  width: 100%;
}

.multiple-select.variant-bordered {
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 40px;
}

.select-trigger.variant-form {
  padding: 4px 16px;
  height: 40px;
  border: 1px solid #6c737f;
  gap: 16px;
}

.select-trigger.variant-bordered {
  height: auto;
  min-height: auto;
  border: 1px solid #212121;
  border-radius: 8px;
  gap: 0;
}

.select-trigger:hover {
  border-color: var(--blue-dark-2);
}

.select-trigger.variant-form:hover {
  border-color: #6c737f;
}

.select-trigger.active {
  border-color: var(--blue-dark-2);
  background-color: var(--blue-dark-2);
}

.select-trigger.variant-form.active {
  border-color: #6c737f;
  background-color: white;
}

.select-trigger.variant-bordered.active {
  background-color: white;
}

.select-trigger.active .select-text,
.select-trigger.active .dropdown-arrow {
  color: #ffffff;
}

.prefix-text {
  font-weight: 400;
  color: var(--primary-blue);
  white-space: nowrap;
}

.select-trigger.active .prefix-text {
  color: #ffffff;
}

.select-trigger.variant-form.active .prefix-text,
.select-trigger.variant-bordered.active .prefix-text {
  color: #3b4648;
}

.select-trigger.variant-form.active .select-text,
.select-trigger.variant-form.active .dropdown-arrow {
  color: #3b4648;
}

.select-trigger.variant-bordered.active .select-text,
.select-trigger.variant-bordered.active .dropdown-arrow {
  color: #3b4648;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-button {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.1px;
  color: var(--primary-pink, #e00069);
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 10px 16px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}

.clear-button:hover {
  opacity: 0.7;
}

.clear-button:active {
  opacity: 0.5;
}

.item-counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: var(--primary-pink);
  padding-top: 2px;
  color: white;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
  animation: counter-appear 0.2s ease-out;
}

.select-trigger.active .item-counter {
  background: #ffffff;
  color: var(--primary-blue);
}

.select-text {
  font-size: var(--fs-big);
  font-weight: 400;
  color: var(--neutral-700);
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-trigger.variant-form .select-text {
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}

.select-trigger.variant-bordered .select-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
}

.dropdown-arrow {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.select-trigger.variant-form .dropdown-arrow {
  color: #6c737f;
}

.select-trigger.variant-bordered .dropdown-arrow {
  color: #6c737f;
  flex-shrink: 0;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 9999;
  width: 360px;
  height: auto;
}

.variant-form .select-dropdown {
  border: 1px solid #6c737f;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
}

.variant-bordered .select-dropdown {
  border-radius: 8px;
  box-shadow: none;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.search-input-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
  height: 40px;
  padding: 4px 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.search-input-wrapper:focus-within {
  border-color: var(--primary-blue, #003d6a);
}

.search-input-wrapper svg {
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  outline: none;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #3b4648;
  background: transparent;
}

.search-input::placeholder {
  color: #98a2b3;
}

.options-container {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
}

.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.options-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 12px;
}

.variant-form .select-option {
  padding: 12px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
}

.variant-bordered .select-option {
  padding: 12px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.variant-bordered .select-option:last-child {
  border-bottom: none;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover:not(.disabled) {
  background-color: #f9fafb;
}

.variant-form .select-option:hover:not(.disabled) {
  background-color: #f2f3fb;
}

.variant-bordered .select-option:hover:not(.disabled) {
  background-color: white;
}

.empty-state {
  justify-content: center;
  cursor: default !important;
  color: #3b4648 !important;
  pointer-events: none;
}

.empty-state:hover {
  background-color: transparent !important;
}

.select-option.selected {
  background-color: #f2f3fb;
  color: #0369a1;
}

.select-option.selected:hover {
  background-color: #f2f3fb;
}

.variant-bordered .select-option.selected {
  background-color: #e91e63;
  color: #ffffff;
  border-bottom-color: #e91e63;
}

.variant-bordered .select-option.selected:hover {
  background-color: #e91e63;
  color: #ffffff;
}

.variant-bordered .select-option.selected .option-text {
  color: #ffffff;
}

.select-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  cursor: pointer;
}

.option-content input[type="checkbox"] {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.2s ease;
  accent-color: var(--primary-blue);
}

.option-content input[type="checkbox"]:checked {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
}

.variant-bordered .option-content input[type="checkbox"]:checked {
  background-color: #ffffff;
  border-color: #ffffff;
  accent-color: #ffffff;
}

.option-text {
  flex: 1;
  font-size: var(--fs-big);
  font-weight: 400;
  color: var(--primary-blue);
  cursor: pointer;
  user-select: none;
}

.variant-bordered .select-option.selected .option-text {
  color: #ffffff;
}

/* Animations */
@keyframes counter-appear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease;
}

.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Clear button animation */
.clear-button-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-button-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.clear-button-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.clear-button-enter-to {
  opacity: 1;
  transform: scale(1);
}

.clear-button-leave-from {
  opacity: 1;
  transform: scale(1);
}

.clear-button-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .select-trigger {
    padding: 8px 10px;
    gap: 6px;
    min-height: 36px;
  }

  .select-dropdown {
    min-width: 200px;
  }

  .item-counter {
    min-width: 18px;
    height: 18px;
    font-size: 11px;
  }

  .dropdown-arrow {
    width: 10px;
    height: 6px;
  }

  .search-input {
    padding: 10px;
    font-size: 13px;
  }

  .select-option {
    padding: 10px;
  }
}

@media (max-width: 480px) {
  .select-trigger {
    padding: 6px 8px;
    gap: 4px;
    min-height: 32px;
  }

  .select-dropdown {
    min-width: 180px;
  }

  .item-counter {
    min-width: 16px;
    height: 16px;
    font-size: 10px;
  }
}
</style>
